const DIGRAPHS = 'ぁぃぅぇぉゃゅょゎゕゖァィゥェォャュョヮヵヶ';

export const DOT_RADIUS = 0.2;
export const STROKE_WIDTH = 0.1;

/** Tokyo-style pitch accent colours (match CAS / WaniKani conventions). */
export function pitchColor(num: number, mora: number): string {
  if (num === 0) return '#d20ca3'; // heiban
  if (num === 1) return '#EA9316'; // atamadaka
  if (num === mora) return '#0cd24d'; // odaka
  if (num < mora) return '#27a2ff'; // nakadaka
  return '#CCCCCC';
}

/**
 * Split a kana reading into mora units (small ゃゅょ etc. attach to the previous).
 */
export function splitMorae(reading: string): string[] {
  const chars = [...(reading || '')];
  const morae: string[] = [];
  for (const char of chars) {
    if (DIGRAPHS.includes(char) && morae.length) {
      morae[morae.length - 1] += char;
    } else {
      morae.push(char);
    }
  }
  return morae;
}

export function moraCount(reading: string): number {
  return splitMorae(reading).length;
}

/** Human label for a Tokyo-style pitch pattern number. */
export function pitchPatternName(pitchNum: number, mora: number): string {
  if (pitchNum === 0) return 'heiban';
  if (pitchNum === 1) return 'atamadaka';
  if (mora > 0 && pitchNum === mora) return 'odaka';
  if (pitchNum > 1 && pitchNum < mora) return 'nakadaka';
  return `drop ${pitchNum}`;
}

export type PitchVariant = {
  num: number;
  name: string;
  color: string;
};

export function describePitchVariants(
  accents: number[] | null | undefined,
  reading = '',
): PitchVariant[] {
  const mora = moraCount(reading);
  return (Array.isArray(accents) ? accents : [])
    .map(n => Number(n))
    .filter(n => Number.isFinite(n) && n >= 0)
    .map(num => ({
      num,
      name: pitchPatternName(num, mora),
      color: pitchColor(num, mora),
    }));
}

export type PitchPoint = { x: number; y: number };

export type PitchSvgProps = {
  viewBox: string;
  color: string;
  points: PitchPoint[];
  morae: string[];
  moraLabelY: number;
};

/** Mora labels sit this far below the low pitch line (y = 1). */
const MORA_LABEL_OFFSET = 0.8;

export function buildPitchSvgProps(
  pitchNum: number,
  reading: string,
  showMoraLabels: boolean,
): PitchSvgProps {
  const morae = splitMorae(reading);
  const mora = morae.length;
  const width = Math.max(mora, 1);
  const color = pitchColor(pitchNum, mora);
  const xCoords = Array.from({ length: mora + 1 }, (_, i) => i);
  const yCoords = new Array(mora + 1).fill(null).map((_, i) => {
    if (pitchNum === 0) return i === 0 ? 1 : 0;
    if (i + 1 === pitchNum) return 0;
    if (i === 0) return 1;
    return i < pitchNum ? 0 : 1;
  });
  const points = yCoords.map((y, i) => ({ x: xCoords[i] ?? i, y }));
  const top = -0.35;
  const height = showMoraLabels ? 2.6 : 1.5;
  return {
    viewBox: `-0.5 ${top} ${width + 1} ${height}`,
    color,
    points,
    morae,
    moraLabelY: 1 + MORA_LABEL_OFFSET,
  };
}

/** True when accents + reading are enough to draw diagrams. */
export function hasRenderablePitchAccent(
  accents: number[] | null | undefined,
  reading: string | null | undefined,
): boolean {
  return Boolean(reading && describePitchVariants(accents, reading).length);
}

const KANA_ONLY = /^[\u3041-\u309F\u30A1-\u30FA\u30FC-\u30FF\uFF66-\uFF9F]+$/;

// ASCII, CJK and fullwidth punctuation, plus whitespace.
const PUNCTUATION = /[\s\u3000-\u303F\uFF01-\uFF20!-/:-@[-`{-~]/g;

/** The reading columns Japanese courses use to carry the kana of an item. */
const KANA_LABELS = new Set([
  'furigana',
  'ふりがな',
  'kana',
  'かな',
  'カナ',
  'hiragana',
  'ひらがな',
  'katakana',
  'カタカナ',
  'reading',
  'よみ',
  '読み',
]);

export function isKanaLabel(label: string): boolean {
  return KANA_LABELS.has(label.trim().toLowerCase());
}

/**
 * A value is only usable as a reading when everything but punctuation is kana,
 * so partially transcribed values never produce a misleading contour.
 */
export function extractKanaReading(value: string | null | undefined): string | null {
  const kana = (value ?? '').replace(PUNCTUATION, '');
  return kana && KANA_ONLY.test(kana) ? kana : null;
}

/**
 * The learning sessions API only sends `kana` for some content, so fall back to
 * the item's furigana column and then to the item itself when it is already kana.
 */
export function resolveKanaReading(
  kana: string | null | undefined,
  extraInfo: { label: string; value: string }[] = [],
  itemValue?: string | null,
): string | null {
  return (
    extractKanaReading(kana) ??
    extractKanaReading(extraInfo.find(info => isKanaLabel(info.label))?.value) ??
    extractKanaReading(itemValue)
  );
}
